export const getCurrentUrl = () => {
  const url = window.location.href
  if (url.at(url.length - 1) === '/') {
    return url.slice(0, -1)
  }
  return url
}

export const isHttpUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Carry the Console auth-shell's return through to the Keycloak BFF. The BFF remains the
 * trust boundary and accepts only relative paths or configured Console/DARIUS HTTPS origins.
 */
export const createKeycloakAuthShellLoginUrl = (
  loginUrl: string,
  search: string,
): string => {
  const returnTo = new URLSearchParams(search).get('redirect')
  if (!returnTo) return loginUrl

  const target = new URL(loginUrl)
  target.searchParams.set('redirect', returnTo)
  return target.toString()
}

export const buildPostLoginRedirectUrl = (url: string, currentPath: string) => {
  return url
  // NOTE: i removed this because i put the home screen behind auth,
  // so this is likely no longer needed (09/10/2024)
  // const pathname = currentPath.trim()
  // const postLoginRedirectUrl =
  //   pathname === ''
  //     ? `${url}/welcome`
  //     : pathname === '/'
  //       ? `${url}welcome`
  //       : url
  // return postLoginRedirectUrl
}

export const getUrlHostname = (url: string) => {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
    return urlObj.hostname
  } catch {
    // Fallback for invalid URLs
    return url.replace(/^https?:\/\//, '').split('/')[0]
  }
}

// Display variant: keeps the port (127.0.0.1:8000 stays distinguishable from
// 127.0.0.1). Favicon lookups keep using getUrlHostname — Google's endpoint
// wants a bare hostname.
export const getUrlHost = (url: string) => {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
    return urlObj.host
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0]
  }
}

// Favicon requests snap to canonical sizes so the same hostname resolves to
// the same URL across DPRs and surfaces (pill, card, search row) — distinct
// sz= values defeat both the browser cache and in-page request coalescing.
const FAVICON_SIZE_BUCKETS = [32, 64, 128, 256]

const faviconUrlCache = new Map<string, string>()

export const getIconFromHostname = (
  hostname: string,
  logicalSize: number,
): string | undefined => {
  if (!hostname) return undefined

  const dpr =
    typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio
      : 1

  const requestedSize = logicalSize * dpr
  const bucket =
    FAVICON_SIZE_BUCKETS.find((size) => size >= requestedSize) ??
    FAVICON_SIZE_BUCKETS[FAVICON_SIZE_BUCKETS.length - 1]

  const cacheKey = `${hostname}|${bucket}`
  const cached = faviconUrlCache.get(cacheKey)
  if (cached) return cached

  const url = `https://www.google.com/s2/favicons?domain=${hostname}&sz=${bucket}`
  faviconUrlCache.set(cacheKey, url)
  return url
}

export const getFaviconUrl = (
  url: string,
  logicalSize: number,
): string | undefined => {
  const hostname = getUrlHostname(url)
  return getIconFromHostname(hostname, logicalSize)
}

export const ensureAbsoluteUrl = (url: string): string => {
  const trimmed = url.trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (trimmed.startsWith('//')) return `https:${trimmed}`
  return `https://${trimmed}`
}
